import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(24)
  .regex(/^[a-z0-9_]+$/, "Letters, numbers, and underscores only");

// Public read of username availability — uses publishable-key client
export const checkUsernameAvailable = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ username: usernameSchema }).parse(input),
  )
  .handler(async ({ data }) => {
    const client = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: profile }, { data: reserved }] = await Promise.all([
      client.from("profiles").select("id").eq("username", data.username).maybeSingle(),
      supabaseAdmin.rpc("is_username_reserved", { _candidate: data.username }),
    ]);
    return { available: !profile && !reserved };
  });

const ProfileUpdateSchema = z.object({
  username: usernameSchema.optional(),
  display_name: z.string().trim().min(1).max(40).nullable().optional(),
  country: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, "Two-letter ISO country code")
    .nullable()
    .optional(),
  avatar_url: z.string().url().max(2048).nullable().optional(),
  bio: z.string().max(160).nullable().optional(),
  tcg_specialisations: z.array(z.string().max(40)).max(10).optional(),
});

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ProfileUpdateSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update(data)
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });
